import React, { createContext, useEffect, useState } from 'react';

type ThemeProviderContextType = {
  isLight: boolean;
  toggleTheme: () => void;
};

type ThemeProviderProps = {
  children?: React.ReactNode;
};

export const ThemeContext = createContext<ThemeProviderContextType>({
  isLight: true,
  toggleTheme: () => {},
});

const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
}: ThemeProviderProps) => {
  const [isLight, setIsLight] = useState(true);

  const toggleTheme = () => {
    setIsLight(!isLight);
  };

  // Handler method for changing theme.
  const handleThemeToggle = (event: MediaQueryList | MediaQueryListEvent) => {
    // If the preferred theme is light, but dark is active, or vice-versa.
    if ((event.matches && !isLight) || (!event.matches && isLight)) {
      toggleTheme();
    }
  };
  // Detecting preferred system theme on load. First, grab light theme query..
  const lightThemeMq = window.matchMedia('(prefers-color-scheme: light)');
  // ..then on load switch to preferred theme..
  useEffect(() => handleThemeToggle(lightThemeMq), []); // eslint-disable-line
  // ..and always change if theme changes while page is open (real-time update).
  lightThemeMq.addEventListener('change', handleThemeToggle);

  // Add className dark to body
  useEffect(() => {
    const body = document.body;
    if (body && !isLight) {
      body.classList.add('dark');
    } else if (body && isLight) {
      body.classList.remove('dark');
    }
  }, [isLight]);

  return (
    <ThemeContext.Provider value={{ isLight, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
